/**
 * Catálogo (U4 — EDITÁVEL): Eventos com busca/filtro e CRUD guiado (criar via
 * modal, editar com flags em Switch, desativar/excluir com confirmação e
 * proteções da API explicadas) e Fórmulas com documentação pt-BR (o que
 * calcula, base legal, parâmetros consumidos com link para Parâmetros) e edição
 * de metadados (descrição/vigência com gate).
 */
import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Group, SegmentedControl, Switch, Tabs, Text, TextInput, ThemeIcon, Title } from "@mantine/core";
import { IconFunction } from "@tabler/icons-react";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { api } from "../api";
import { fmtCompetencia } from "../format";
import { ErroApiAlert, Confirmacao, notificarOk, notificarErroApi } from "../componentes/notificacoes";
import { Carregando, Vazio } from "../componentes/Estados";
import { ItemEntidade } from "../componentes/listas";
import { EventoDetalhe, type Evento } from "./catalogo/EventoDetalhe";
import { FormulaDetalhe, type Formula } from "./catalogo/FormulaDetalhe";

/** Incidência como badge (sim/não/—), nunca texto cru. */
function FlagBadge({ v, rotulo }: { v: boolean | null; rotulo: string }) {
  if (v === null) {
    return (
      <Badge variant="outline" color="gray" size="sm" title={`${rotulo}: não mapeado`}>
        —
      </Badge>
    );
  }
  return (
    <Badge variant="light" color={v ? "sillis" : "gray"} size="sm" title={rotulo}>
      {v ? "✓" : "✗"}
    </Badge>
  );
}

export function CatalogoPage() {
  const [eventos, setEventos] = useState<Evento[] | null>(null);
  const [formulas, setFormulas] = useState<Formula[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [busca, setBusca] = useState("");
  const [segTipo, setSegTipo] = useState("todos");
  const [soAtivos, setSoAtivos] = useState(true);
  const [eventoEditando, setEventoEditando] = useState<Evento | null>(null);
  const [criando, setCriando] = useState(false);
  const [formulaDetalhe, setFormulaDetalhe] = useState<string | null>(null);
  const [confirmacaoAcao, setConfirmacaoAcao] = useState<{ tipo: "desativar" | "excluir"; evento: Evento } | null>(null);

  async function carregar() {
    try {
      setErro(null);
      const [e, f] = await Promise.all([api.get<{ eventos: Evento[] }>("/eventos"), api.get<{ formulas: Formula[] }>("/formulas")]);
      setEventos(e.eventos);
      setFormulas(f.formulas);
    } catch (err) {
      setErro(err);
    }
  }

  useEffect(() => {
    void carregar();
  }, []);

  async function executarAcao() {
    if (!confirmacaoAcao) return;
    const { tipo, evento } = confirmacaoAcao;
    try {
      if (tipo === "desativar") {
        await api.post(`/eventos/${evento.id}/desativar`);
        notificarOk(`"${evento.descricao}" desativado (reversível).`);
      } else {
        await api.del(`/eventos/${evento.id}`);
        notificarOk(`"${evento.descricao}" excluído (nunca tinha sido usado).`);
      }
      setConfirmacaoAcao(null);
      await carregar();
    } catch (e) {
      setConfirmacaoAcao(null);
      notificarErroApi(e);
    }
  }

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return (eventos ?? []).filter((e) => {
      if (soAtivos && !e.ativo) return false;
      if (segTipo !== "todos" && (e.tipo ?? "") !== segTipo) return false;
      if (!q) return true;
      return (
        e.descricao.toLowerCase().includes(q) ||
        String(e.codigoDominio ?? "").includes(q) ||
        String(e.idConvenia ?? "").includes(q)
      );
    });
  }, [eventos, busca, segTipo, soAtivos]);

  const formulasPorChave = useMemo(() => {
    const mapa = new Map<string, Formula[]>();
    for (const f of formulas ?? []) {
      const g = mapa.get(f.chave) ?? [];
      g.push(f);
      mapa.set(f.chave, g);
    }
    return [...mapa.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [formulas]);

  return (
    <>
      <Group justify="space-between" mb={4}>
        <Title order={2}>Eventos &amp; Fórmulas</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setCriando(true)}>
          Novo evento
        </Button>
      </Group>
      <Text size="sm" c="dimmed" mb="md">
        Rubricas da folha (eventos) e as fórmulas que as calculam — clique para abrir/editar; criação e exclusão com proteções.
      </Text>
      <ErroApiAlert erro={erro} />

      <Tabs defaultValue="eventos" keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="eventos">Eventos ({eventos?.length ?? "…"})</Tabs.Tab>
          <Tabs.Tab value="formulas">Fórmulas ({formulas?.length ?? "…"})</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="eventos">
          <Group mb="sm" wrap="wrap" align="flex-end">
            <TextInput
              placeholder="buscar por descrição ou código…"
              leftSection={<IconSearch size={14} />}
              value={busca}
              onChange={(e) => setBusca(e.currentTarget.value)}
              w={300}
            />
            <SegmentedControl
              value={segTipo}
              onChange={setSegTipo}
              data={[
                { value: "todos", label: "Todos" },
                { value: "P", label: "Proventos" },
                { value: "D", label: "Descontos" },
                { value: "", label: "Sem tipo" },
              ]}
            />
            <Switch label="só ativos" checked={soAtivos} onChange={(e) => setSoAtivos(e.currentTarget.checked)} />
          </Group>
          {!eventos ? (
            <Carregando linhas={6} />
          ) : filtrados.length === 0 ? (
            <Vazio texto="Nenhum evento com esses filtros." acao="Limpar filtros" onAcao={() => { setBusca(""); setSegTipo("todos"); setSoAtivos(false); }} />
          ) : (
            filtrados.map((e) => (
              <ItemEntidade
                key={e.id}
                ariaLabel={`Editar o evento ${e.descricao}`}
                onClick={() => setEventoEditando(e)}
                opaco={!e.ativo}
                icone={
                  <Badge size="lg" variant="light" color={e.tipo === "P" ? "sillis" : e.tipo === "D" ? "red" : "gray"} ff="monospace">
                    {e.codigoDominio ?? "s/c"}
                  </Badge>
                }
                titulo={
                  <>
                    {e.descricao}
                    {!e.ativo && (
                      <Badge ml="xs" size="xs" color="gray">
                        inativo
                      </Badge>
                    )}
                  </>
                }
                linhaSecundaria={`Convenia ${e.idConvenia ?? "—"} · ${e.unidade ?? "sem unidade"} · ${e.formula?.versao ?? "sem fórmula"}`}
                badges={
                  <>
                    <FlagBadge v={e.incideInss} rotulo="Incide INSS" />
                    <FlagBadge v={e.incideIrrf} rotulo="Incide IRRF" />
                    <FlagBadge v={e.incideFgts} rotulo="Incide FGTS" />
                    {(e.exportaDominio || e.exportaConvenia) && (
                      <Badge variant="outline" color="gray" size="sm">
                        {[e.exportaDominio ? "Domínio" : "", e.exportaConvenia ? "Convenia" : ""].filter(Boolean).join("+")}
                      </Badge>
                    )}
                  </>
                }
                acoes={[
                  { rotulo: "Editar", onClick: () => setEventoEditando(e) },
                  ...(e.ativo
                    ? [{ rotulo: "Desativar", cor: "yellow.8", onClick: () => setConfirmacaoAcao({ tipo: "desativar", evento: e }) }]
                    : []),
                  { rotulo: "Excluir (se nunca usado)", cor: "red.7", onClick: () => setConfirmacaoAcao({ tipo: "excluir", evento: e }) },
                ]}
              />
            ))
          )}
        </Tabs.Panel>

        <Tabs.Panel value="formulas">
          {!formulas ? (
            <Carregando linhas={5} />
          ) : (
            formulasPorChave.map(([chave, versoes]) => (
              <div key={chave}>
                <Title order={4} mt="md" mb={4}>
                  {chave}
                </Title>
                {versoes.map((f) => (
                  <ItemEntidade
                    key={f.id}
                    ariaLabel={`Detalhe da fórmula ${f.versao}`}
                    onClick={() => setFormulaDetalhe(f.id === formulaDetalhe ? null : f.id)}
                    icone={
                      <ThemeIcon variant="light" size="lg">
                        <IconFunction size={18} />
                      </ThemeIcon>
                    }
                    titulo={<Text span ff="monospace" size="sm">{f.versao}</Text>}
                    linhaSecundaria={`${f.descricao ?? "sem descrição"} · ${fmtCompetencia(f.vigenciaInicio)} → ${f.vigenciaFim ? fmtCompetencia(f.vigenciaFim) : "em vigor"}`}
                    badges={
                      formulaDetalhe === f.id ? (
                        <Badge variant="light" size="sm">
                          aberto
                        </Badge>
                      ) : undefined
                    }
                  />
                ))}
                {formulaDetalhe && versoes.some((f) => f.id === formulaDetalhe) && (
                  <FormulaDetalhe formula={versoes.find((f) => f.id === formulaDetalhe)!} onFechar={() => setFormulaDetalhe(null)} onSalvo={carregar} />
                )}
              </div>
            ))
          )}
        </Tabs.Panel>
      </Tabs>

      <Confirmacao
        aberta={confirmacaoAcao !== null}
        titulo={confirmacaoAcao?.tipo === "desativar" ? `Desativar "${confirmacaoAcao.evento.descricao}"?` : `Excluir "${confirmacaoAcao?.evento.descricao ?? ""}"?`}
        consequencia={
          confirmacaoAcao?.tipo === "desativar"
            ? "O evento some das telas de seleção, mas continua no histórico (runs e exports antigos não mudam). Reversível editando depois."
            : "Só funciona se o evento NUNCA foi usado em nenhuma execução — a API confere e bloqueia com explicação se já tiver uso (nesse caso o caminho é desativar)."
        }
        rotuloConfirmar={confirmacaoAcao?.tipo === "desativar" ? "Desativar" : "Excluir"}
        onConfirmar={() => void executarAcao()}
        onCancelar={() => setConfirmacaoAcao(null)}
      />

      {(eventoEditando || criando) && (
        <EventoDetalhe
          evento={criando ? null : eventoEditando}
          formulas={(formulas ?? []).map((f) => ({ id: f.id, chave: f.chave, versao: f.versao }))}
          onFechar={() => {
            setEventoEditando(null);
            setCriando(false);
          }}
          onSalvo={carregar}
        />
      )}
    </>
  );
}
